import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useToast } from '../../../Context/ToastContext';
import { ModalOverlay, Modal, ModalTitle, ModalActions, ModalButton } from './CaseFileStyles';
import { Icons } from './caseFileConstants';

// ============================================
// PROFILE EDIT MODAL STYLED COMPONENTS
// ============================================

const ProfileEditModalOverlay = styled(ModalOverlay)``;

const ProfileEditModalContainer = styled(Modal)`
  min-width: 600px;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
`;

const ProfileEditContent = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

const ProfileFieldRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 30px;
  gap: 12px;
  margin-bottom: 10px;
  align-items: center;
`;

const ProfileFieldLabel = styled.label`
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
`;

const ProfileFieldInput = styled.input`
  padding: 10px 12px;
  background: rgba(31, 41, 55, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.5);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0 8px 0;
  margin-top: 8px;
  border-top: 1px solid rgba(75, 85, 99, 0.3);
  
  &:first-child {
    margin-top: 0;
    border-top: none;
    padding-top: 0;
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: #a5b4fc;
  }
  
  span {
    font-size: 13px;
    font-weight: 600;
    color: #e5e7eb;
  }
`;

const DeleteFieldButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const AddFieldSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed rgba(75, 85, 99, 0.5);
`;

const AddFieldRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 12px;
  align-items: center;
`;

const AddFieldButton = styled.button`
  padding: 8px 16px;
  background: rgba(99, 102, 241, 0.2);
  border: 1px dashed rgba(99, 102, 241, 0.5);
  border-radius: 6px;
  color: #a5b4fc;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    border-color: rgba(99, 102, 241, 0.7);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const FieldCount = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-left: auto;
`;

// Helper: Convert camelCase to Title Case
const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Helper: Detect category from field name
const detectCategory = (fieldName) => {
  const lower = fieldName.toLowerCase();
  
  // Personal
  if (['firstname', 'lastname', 'fullname', 'name', 'dateofbirth', 'dob', 'gender', 'maritalstatus', 'age'].some(k => lower.includes(k))) {
    return 'personal';
  }
  
  // Contact
  if (['email', 'phone', 'address', 'city', 'country', 'zip', 'postal', 'street', 'telephone', 'mobile'].some(k => lower.includes(k))) {
    return 'contact';
  }
  
  // Employment
  if (['occupation', 'employer', 'job', 'work', 'company', 'position', 'salary', 'income', 'industry', 'employment'].some(k => lower.includes(k))) {
    return 'employment';
  }
  
  // Education
  if (['education', 'degree', 'school', 'university', 'college', 'institution', 'study', 'major', 'graduation'].some(k => lower.includes(k))) {
    return 'education';
  }
  
  // Immigration (specific to this domain)
  if (['immigration', 'visa', 'passport', 'permit', 'status', 'target', 'category', 'nationality', 'citizenship', 'origin', 'birth'].some(k => lower.includes(k))) {
    return 'immigration';
  }
  
  // Language
  if (['language', 'english', 'french', 'spanish', 'ielts', 'toefl', 'celpip', 'tef'].some(k => lower.includes(k))) {
    return 'language';
  }
  
  // Family
  if (['spouse', 'family', 'dependent', 'children', 'wife', 'husband', 'married', 'partner'].some(k => lower.includes(k))) {
    return 'family';
  }
  
  // Finance
  if (['bank', 'account', 'fund', 'asset', 'invest', 'credit', 'loan', 'mortgage', 'net worth', 'savings'].some(k => lower.includes(k))) {
    return 'finance';
  }
  
  return 'other';
};

const categoryInfo = {
  personal: { icon: 'user', label: 'Personal Information' },
  contact: { icon: 'mail', label: 'Contact Details' },
  employment: { icon: 'briefcase', label: 'Employment' },
  education: { icon: 'book', label: 'Education' },
  immigration: { icon: 'globe', label: 'Immigration' },
  language: { icon: 'globe', label: 'Languages' },
  family: { icon: 'user', label: 'Family' },
  finance: { icon: 'briefcase', label: 'Finance' },
  other: { icon: 'file', label: 'Other Information' }
};

const ProfileEditModal = ({ onClose, threadId, authenticatedAxios, onSave, existingData }) => {
  const { showToast } = useToast();
  
  // Initialize formData from ALL existing fields dynamically
  const [formData, setFormData] = useState(() => {
    const data = {};
    if (existingData && typeof existingData === 'object') {
      Object.entries(existingData).forEach(([key, value]) => {
        // Only include string/number values, skip objects and nulls
        if (value !== null && value !== undefined && typeof value !== 'object') {
          data[key] = String(value);
        } else if (typeof value === 'object' && value !== null) {
          // Try to extract string from object
          const str = value.title || value.name || value.value || '';
          if (str) data[key] = String(str);
        }
      });
    }
    return data;
  });
  
  const [saving, setSaving] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [showAddField, setShowAddField] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteField = (field) => {
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[field];
      return newData;
    });
  };

  const handleAddField = () => {
    if (!newFieldName.trim()) return;
    
    // Convert to camelCase for consistency
    const fieldKey = newFieldName
      .trim()
      .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
      .replace(/^(.)/, c => c.toLowerCase());
    
    setFormData(prev => ({ ...prev, [fieldKey]: newFieldValue }));
    setNewFieldName('');
    setNewFieldValue('');
    setShowAddField(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty values
      const cleanedData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim()) {
          cleanedData[key] = value.trim();
        }
      });
      
      await authenticatedAxios.post(`/analysis/update-profile/${threadId}`, {
        clientProfile: cleanedData
      });
      onSave();
    } catch (error) {
      console.error('Error saving profile:', error);
      showToast('Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Group fields by category
  const groupedFields = useMemo(() => {
    const groups = {};
    Object.keys(formData).forEach(field => {
      const category = detectCategory(field);
      if (!groups[category]) groups[category] = [];
      groups[category].push(field);
    });
    
    // Sort categories by importance
    const categoryOrder = ['personal', 'contact', 'employment', 'education', 'immigration', 'language', 'family', 'finance', 'other'];
    const sortedGroups = {};
    categoryOrder.forEach(cat => {
      if (groups[cat]) sortedGroups[cat] = groups[cat];
    });
    
    return sortedGroups;
  }, [formData]);

  const totalFields = Object.keys(formData).length;

  return (
    <ProfileEditModalOverlay onClick={onClose}>
      <ProfileEditModalContainer onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <ModalTitle style={{ margin: 0 }}>Edit Client Profile</ModalTitle>
          <FieldCount>{totalFields} field{totalFields !== 1 ? 's' : ''}</FieldCount>
        </div>
        
        <ProfileEditContent>
          {totalFields === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
              <p>No profile data yet.</p>
              <p style={{ fontSize: '12px' }}>Click "Add Field" to start building the profile.</p>
            </div>
          ) : (
            Object.entries(groupedFields).map(([category, fields]) => (
              <div key={category}>
                <CategoryHeader>
                  {Icons[categoryInfo[category].icon]}
                  <span>{categoryInfo[category].label}</span>
                  <FieldCount>({fields.length})</FieldCount>
                </CategoryHeader>
                
                {fields.map(field => (
                  <ProfileFieldRow key={field}>
                    <ProfileFieldLabel title={field}>
                      {formatFieldName(field)}
                    </ProfileFieldLabel>
                    <ProfileFieldInput 
                      value={formData[field] || ''}
                      onChange={e => handleChange(field, e.target.value)}
                      placeholder={`Enter ${formatFieldName(field).toLowerCase()}`}
                    />
                    <DeleteFieldButton 
                      onClick={() => handleDeleteField(field)}
                      title="Remove field"
                    >
                      {Icons.trash}
                    </DeleteFieldButton>
                  </ProfileFieldRow>
                ))}
              </div>
            ))
          )}
          
          {/* Add New Field Section */}
          <AddFieldSection>
            {showAddField ? (
              <AddFieldRow>
                <ProfileFieldInput
                  value={newFieldName}
                  onChange={e => setNewFieldName(e.target.value)}
                  placeholder="Field name (e.g., Passport Number)"
                  style={{ fontSize: '12px' }}
                />
                <ProfileFieldInput
                  value={newFieldValue}
                  onChange={e => setNewFieldValue(e.target.value)}
                  placeholder="Value"
                  onKeyPress={e => e.key === 'Enter' && handleAddField()}
                />
                <div style={{ display: 'flex', gap: '4px' }}>
                  <ModalButton 
                    style={{ padding: '6px 10px', fontSize: '11px' }}
                    onClick={() => setShowAddField(false)}
                  >
                    Cancel
                  </ModalButton>
                  <ModalButton 
                    $primary 
                    style={{ padding: '6px 10px', fontSize: '11px' }}
                    onClick={handleAddField}
                  >
                    Add
                  </ModalButton>
                </div>
              </AddFieldRow>
            ) : (
              <AddFieldButton onClick={() => setShowAddField(true)}>
                {Icons.plus}
                Add Custom Field
              </AddFieldButton>
            )}
          </AddFieldSection>
        </ProfileEditContent>
        
        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton $primary onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </ModalButton>
        </ModalActions>
      </ProfileEditModalContainer>
    </ProfileEditModalOverlay>
  );
};

export default ProfileEditModal;
